import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { studentApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  ItemCard,
  LoadingState,
  Modal,
  PageHeader,
  Textarea,
  useToast,
} from '@/components/ui';
import { DiplomaPanel } from '@/features/verification/DiplomaPanel';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';

import { educationSchema } from '@/validations';
import { formatMonthYear } from '@/utils/format';

export function StudentEducationPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['student-education'],
    queryFn: studentApi.listEducation,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['student-education'] });
    void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['student-profile'] });
  }

  const remove = useMutation({
    mutationFn: (id) => studentApi.deleteEducation(id),
    onSuccess: () => {
      toast.success('Education removed');
      setDeleting(null);
      invalidate();
    },
    onError: (mutationError) => toast.error(errorMessage(mutationError)),
  });

  if (isPending) return <LoadingState label="Loading your education…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  return (
    <div>
      <PageHeader
        title="Education"
        description="Degrees, programmes and courses you have taken. Upload a certificate to get an entry verified."
        action={
          <Button onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} />
            Add education
          </Button>
        }
      />

      {data.length === 0 ? (
        <EmptyState
          icon={<Icon name="university" size={22} />}
          title="No education added yet"
          description="Companies look for your degree programme first."
          action={<Button onClick={() => setCreating(true)}>Add your first entry</Button>}
        />
      ) : (
        <ul className="space-y-3">
          {data.map((entry) => (
            <ItemCard
              key={entry.id}
              icon="university"
              tone={entry.is_current ? 'success' : 'brand'}
              title={entry.degree}
              subtitle={entry.institution_name}
              meta={`${formatMonthYear(entry.start_date)} – ${
                entry.is_current ? 'Present' : formatMonthYear(entry.end_date)
              }`}
              badges={
                <>
                  {entry.is_current && (
                    <Badge tone="success" dot>
                      Current
                    </Badge>
                  )}
                  {entry.grade && <Badge>{entry.grade}</Badge>}
                  {entry.diploma_status === 'VERIFIED' && (
                    <Badge tone="success" dot>
                      Verified
                    </Badge>
                  )}
                </>
              }
              footer={<DiplomaPanel entry={entry} onChanged={invalidate} />}
              actions={
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    title={`Edit ${entry.degree}`}
                    onClick={() => setEditing(entry)}
                  >
                    <Icon name="pencil" size={14} />
                    Edit
                  </Button>
                  <Button
                    variant="ghostDanger"
                    size="sm"
                    title={`Delete ${entry.degree}`}
                    onClick={() => setDeleting(entry)}
                  >
                    <Icon name="trash" size={14} />
                    Delete
                  </Button>
                </>
              }
            >
              {entry.description}
            </ItemCard>
          ))}
        </ul>
      )}

      {/* Keyed per entry (and "new"), so the form starts from that entry's
          values -- or blank -- every time it opens, never from the last one. */}
      <EducationFormModal
        key={editing?.id ?? (creating ? 'new' : 'closed')}
        open={creating || editing !== null}
        entry={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={invalidate}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this education entry?"
        message="This cannot be undone."
        confirmLabel="Delete"
        isLoading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
      />
    </div>
  );
}

function EducationFormModal({ open, entry, onClose, onSaved }) {
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(educationSchema),
    values: entry
      ? {
          institution_name: entry.institution_name,
          degree: entry.degree,
          field_of_study: entry.field_of_study ?? '',
          grade: entry.grade ?? '',
          start_date: entry.start_date,
          end_date: entry.end_date ?? '',
          is_current: entry.is_current,
          description: entry.description ?? '',
        }
      : undefined,
  });
  const isCurrent = watch('is_current');

  async function onSubmit(values) {
    const payload = {
      institution_name: values.institution_name,
      degree: values.degree,
      field_of_study: values.field_of_study || null,
      grade: values.grade || null,
      start_date: values.start_date,
      end_date: values.end_date || null,
      is_current: values.is_current,
      description: values.description || null,
      university_id: null,
    };

    try {
      if (entry) await studentApi.updateEducation(entry.id, payload);
      else await studentApi.addEducation(payload);
      toast.success(entry ? 'Education updated' : 'Education added');
      reset();
      onSaved();
      onClose();
    } catch (error) {
      if (!applyFieldErrors(error, setError)) {
        toast.error(errorMessage(error, 'Could not save the entry'));
      }
    }
  }

  return (
    <Modal
      open={open}
      title={entry ? 'Edit education' : 'Add education'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="education-form" isLoading={isSubmitting}>
            Save
          </Button>
        </>
      }
    >
      <form id="education-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {entry?.diploma_status === 'VERIFIED' && (
          <Alert tone="warning">
            This entry is verified. Changing the institution, degree, field, grade or dates sends
            your certificate back to be checked again.
          </Alert>
        )}
        <Input
          label="Institution"
          required
          error={errors.institution_name?.message}
          {...register('institution_name')}
        />
        <Input label="Degree" required error={errors.degree?.message} {...register('degree')} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Field of study"
            error={errors.field_of_study?.message}
            {...register('field_of_study')}
          />
          <Input label="Grade" error={errors.grade?.message} {...register('grade')} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Start date"
            type="date"
            required
            error={errors.start_date?.message}
            {...register('start_date')}
          />
          <Input
            label="End date"
            type="date"
            required={!isCurrent}
            disabled={isCurrent}
            hint={isCurrent ? 'Not needed while you study here.' : undefined}
            error={errors.end_date?.message}
            {...register('end_date')}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="border-line text-brand-600 size-4 rounded border"
            {...register('is_current', {
              // A current entry has no end date; clear it rather than refuse.
              onChange: (event) => {
                if (event.target.checked) setValue('end_date', '', { shouldValidate: true });
              },
            })}
          />
          <span className="text-ink-muted">I am currently studying here</span>
        </label>
        <Textarea
          label="Description"
          error={errors.description?.message}
          {...register('description')}
        />
      </form>
    </Modal>
  );
}
